import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  ArrowRight,
  ChefHat,
  Heart,
  Leaf,
  Plus,
  ShoppingCart,
  Utensils,
} from "lucide-react";

import {
  fetchMenu,
} from "../../actions/menuActions";

import {
  useCart,
} from "../../components/CartContext";

import "./css/HomeScreen.css";


/* =========================================================
   THEME 9 DECORATIVE IMAGES
========================================================= */

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1547592180-85f173990554?auto=format&fit=crop&w=1400&q=90";

const ABOUT_IMAGE =
  "https://images.unsplash.com/photo-1498837167922-ddd27525d352?auto=format&fit=crop&w=1400&q=90";


/* =========================================================
   IMAGE URL HELPER
========================================================= */

const getImageUrl = (image) => {

  if (!image) {
    return "";
  }


  /*
   * Support image objects if the API
   * ever returns:
   *
   * { url: "..." }
   *
   * instead of a plain string.
   */

  const imageValue =
    typeof image === "string"
      ? image
      : image.url || image.image || "";


  if (!imageValue) {
    return "";
  }


  if (
    imageValue.startsWith("http://") ||
    imageValue.startsWith("https://")
  ) {
    return imageValue;
  }


  return `${
    imageValue.startsWith("/")
      ? ""
      : "/"
  }${imageValue}`;

};


/* =========================================================
   COMPONENT
========================================================= */

function HomeScreen() {

  const dispatch =
    useDispatch();

  const navigate =
    useNavigate();

  const {
    restaurantId,
  } = useParams();


  /* =======================================================
     REDUX
  ======================================================= */

  const {
    categories,
    loading,
    error,
  } = useSelector(
    (state) => state.menu
  );


  /* =======================================================
     CART
  ======================================================= */

  const {
    addToCart,
    cartCount,
  } = useCart();


  /* =======================================================
     STATE
  ======================================================= */

  const [
    activeCategory,
    setActiveCategory,
  ] = useState(null);


  /*
   * Popular slider index.
   *
   * Mobile:
   *
   * 0 = items 1,2,3
   * 3 = items 4,5,6
   * 6 = items 7,8,9
   *
   * Desktop CSS does not use
   * this transform.
   */

  const [
    popularIndex,
    setPopularIndex,
  ] = useState(0);


  /* =======================================================
     FETCH MENU
  ======================================================= */

  useEffect(() => {

    if (!restaurantId) {
      return;
    }


    dispatch(
      fetchMenu(
        restaurantId
      )
    );

  }, [
    dispatch,
    restaurantId,
  ]);


  /* =======================================================
     SORT CATEGORIES + ITEMS
  ======================================================= */

  const sortedCategories =
    useMemo(() => {

      if (
        !Array.isArray(
          categories
        )
      ) {
        return [];
      }


      return [...categories]

        .filter(
          (category) =>
            category.is_active !==
            false
        )

        .sort(
          (a, b) =>
            Number(
              a.display_order || 0
            ) -
            Number(
              b.display_order || 0
            )
        )

        .map(
          (category) => ({

            ...category,

            items: [
              ...(category.items || []),
            ]

              .filter(
                (item) =>
                  item.is_available !==
                  false
              )

              .sort(
                (a, b) =>
                  Number(
                    a.display_order || 0
                  ) -
                  Number(
                    b.display_order || 0
                  )
              ),

          })
        );

    }, [categories]);


  // Select the first category when menu data arrives.
  // A category click displays only its own items.
  useEffect(() => {
    if (sortedCategories.length === 0) {
      setActiveCategory(null);
      return;
    }
    setActiveCategory((previous) =>
      sortedCategories.some((category) => category.id === previous)
        ? previous
        : sortedCategories[0].id
    );
  }, [sortedCategories]);

  /* =======================================================
     POPULAR ITEMS

     First 3 available items
     from every category.
  ======================================================= */

  const popularItems =
    useMemo(() => {

      const result = [];


      sortedCategories.forEach(
        (category) => {

          const firstThree =
            category.items.slice(
              0,
              3
            );


          firstThree.forEach(
            (item) => {

              result.push({

                ...item,

                categoryName:
                  category.name,

              });

            }
          );

        }
      );


      return result;

    }, [sortedCategories]);


  /* =======================================================
     RESET POPULAR SLIDER
  ======================================================= */

  useEffect(() => {

    setPopularIndex(0);

  }, [
    popularItems.length,
  ]);


  /* =======================================================
     POPULAR AUTO SLIDER

     Mobile only.

     Exactly 3 items are displayed
     at once.

     Every 3 seconds move forward
     by another group of 3.
  ======================================================= */

  useEffect(() => {

    if (
      popularItems.length <= 3
    ) {
      return undefined;
    }


    const interval =
      window.setInterval(
        () => {

          /*
           * Only change slider position
           * on mobile.
           */

          if (
            window.innerWidth > 650
          ) {
            return;
          }


          setPopularIndex(
            (previousIndex) => {

              const nextIndex =
                previousIndex + 3;


              /*
               * If there isn't another
               * complete group available,
               * return to the beginning.
               */

              if (
                nextIndex >=
                popularItems.length
              ) {
                return 0;
              }


              return nextIndex;

            }
          );

        },
        3000
      );


    return () => {

      window.clearInterval(
        interval
      );

    };

  }, [
    popularItems.length,
  ]);


  /* =======================================================
     SCROLL HELPER
  ======================================================= */

  const scrollToSection = (
    sectionId
  ) => {

    const element =
      document.getElementById(
        sectionId
      );


    if (!element) {
      return;
    }


    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  };


  /* =======================================================
     SCROLL TO MENU
  ======================================================= */

  const scrollToMenu = () => {

    scrollToSection(
      "theme9-menu"
    );

  };


  /* =======================================================
     SCROLL TO ABOUT
  ======================================================= */

  const scrollToAbout = () => {

    scrollToSection(
      "theme9-about"
    );

  };


  /* =======================================================
     ADD TO CART
  ======================================================= */

  const handleAddToCart = (
    item
  ) => {

    addToCart(item);

  };


  /* =======================================================
     TOTAL MENU ITEMS
  ======================================================= */

  const totalMenuItems =
    useMemo(() => {

      return sortedCategories.reduce(
        (
          total,
          category
        ) =>
          total +
          category.items.length,
        0
      );

    }, [sortedCategories]);


  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {

    return (

      <main className="theme9-loading">

        <div className="theme9-loader" />

        <p>
          Preparing something
          fresh...
        </p>

      </main>

    );

  }


  /* =======================================================
     ERROR
  ======================================================= */

  if (error) {

    return (

      <main className="theme9-error">

        <span>
          MENU
        </span>

        <h2>
          Unable to load menu
        </h2>

        <p>
          {
            typeof error ===
            "string"
              ? error
              : "Please try again."
          }
        </p>

        <button
          type="button"
          onClick={() =>
            dispatch(
              fetchMenu(
                restaurantId
              )
            )
          }
        >
          Try Again
        </button>

      </main>

    );

  }


  /* =======================================================
     PAGE
  ======================================================= */

  return (

    <main className="theme9-home">


      {/* ===================================================
          HERO
      ==================================================== */}

      <section className="theme9-hero">


        {/* DECORATIVE BLOBS */}

        <div
          className="
            theme9-hero-blob
            theme9-blob-one
          "
        />

        <div
          className="
            theme9-hero-blob
            theme9-blob-two
          "
        />

        <div
          className="
            theme9-hero-blob
            theme9-blob-three
          "
        />


        <div className="theme9-hero-inner">


          {/* HERO COPY */}

          <div className="theme9-hero-copy">

            <span className="theme9-eyebrow">
              FRESH • HEALTHY • DELICIOUS
            </span>


            <h1>
              Get the Yum
              <br />
              for Your Tum
            </h1>


            <p>
              Fresh meals prepared
              with quality ingredients
              for every part of your
              day. Tasty, wholesome
              and made with care.
            </p>


            <div className="theme9-hero-actions">

              <button
                type="button"
                className="
                  theme9-primary-button
                "
                onClick={
                  scrollToMenu
                }
              >

                Order Now

                <ArrowRight
                  size={18}
                />

              </button>


              <button
                type="button"
                className="
                  theme9-outline-button
                "
                onClick={
                  scrollToAbout
                }
              >
                Learn More
              </button>

            </div>


            <div className="theme9-review">

              <div className="theme9-review-avatar">

                <Leaf
                  size={24}
                />

              </div>


              <div>

                <strong>
                  Fresh every day
                </strong>

                <p>
                  Delicious food made
                  from carefully
                  selected ingredients.
                </p>

              </div>

            </div>

          </div>


          {/* HERO VISUAL */}

          <div className="theme9-hero-visual">

            <div className="theme9-hero-image-ring">

              <img
                src={HERO_IMAGE}
                alt="Fresh healthy food"
              />

            </div>


            <div className="theme9-menu-count">

              <strong>
                {totalMenuItems}+
              </strong>

              <span>
                menu
                <br />
                variants
              </span>

            </div>


            <div className="theme9-deco-leaf">

              <Leaf />

            </div>

          </div>

        </div>

      </section>


      {/* ===================================================
          BENEFITS
      ==================================================== */}

      <section className="theme9-benefits">


        {/* FRESH INGREDIENTS */}

        <div className="theme9-benefit">

          <div className="theme9-benefit-icon">

            <Leaf />

          </div>

          <strong>
            Fresh Ingredients
          </strong>

          <p>
            Fresh and high quality
            ingredients.
          </p>

        </div>


        {/* EXPERT CHEF */}

        <div className="theme9-benefit">

          <div className="theme9-benefit-icon">

            <ChefHat />

          </div>

          <strong>
            Expert Chef
          </strong>

          <p>
            Prepared with care
            and passion.
          </p>

        </div>

        {/* HEALTHY & TASTY */}

        <div className="theme9-benefit">

          <div className="theme9-benefit-icon">

            <Heart />

          </div>

          <strong>
            Healthy & Tasty
          </strong>

          <p>
            Delicious meals with
            balanced nutrition.
          </p>

        </div>

      </section>


      {/* ===================================================
          POPULAR DISHES

          Header navigation target:
          #theme9-popular
      ==================================================== */}

      <section
        id="theme9-popular"
        className="theme9-popular"
      >

        <div className="theme9-section-header">

          <div>

            <span className="theme9-eyebrow">
              FRESH FROM OUR KITCHEN
            </span>

            <h2>
              Popular Dishes
            </h2>

            <p>
              Customer favourites
              prepared fresh every day.
            </p>

          </div>

        </div>


        {/* =================================================
            POPULAR SLIDER

            ALL popular items stay inside
            one horizontal track.

            Mobile CSS should show exactly
            three cards at a time.

            popularIndex changes:
            0 -> 3 -> 6 -> 9...
        ================================================= */}

        <div className="theme9-popular-viewport">

          <div
            className="
              theme9-popular-grid
            "
            style={{
              "--theme9-popular-index":
                popularIndex,
            }}
          >

            {popularItems.map(
              (
                item,
                index
              ) => {

                const image =
                  getImageUrl(
                    item.image
                  );


                return (

                  <article
                    className="
                      theme9-dish-card
                    "
                    key={
                      `${item.categoryName}-${item.id}-${index}`
                    }
                  >

                    <div className="
                      theme9-dish-image
                    ">

                      {image ? (

                        <img
                          src={image}
                          alt={
                            item.name
                          }
                        />

                      ) : (

                        <div className="
                          theme9-image-placeholder
                        ">

                          <Utensils />

                        </div>

                      )}


                      <span>
                        {
                          item.categoryName
                        }
                      </span>

                    </div>


                    <div className="
                      theme9-dish-content
                    ">

                      <h3>
                        {item.name}
                      </h3>


                      <p>
                        {
                          item.description ||
                          "Freshly prepared with quality ingredients."
                        }
                      </p>


                      <div className="
                        theme9-dish-bottom
                      ">

                        <strong>
                          ₹
                          {Number(
                            item.price ||
                              0
                          ).toFixed(0)}
                        </strong>


                        <button
                          type="button"
                          aria-label={
                            `Add ${item.name} to cart`
                          }
                          onClick={() =>
                            handleAddToCart(
                              item
                            )
                          }
                        >

                          <Plus
                            size={18}
                          />

                          <span>
                            Add
                          </span>

                        </button>

                      </div>

                    </div>

                  </article>

                );

              }
            )}

          </div>

        </div>

      </section>


      {/* ===================================================
          ABOUT

          Header navigation target:
          #theme9-about
      ==================================================== */}

      <section
        id="theme9-about"
        className="theme9-about"
      >

        <div className="theme9-about-image">

          <img
            src={ABOUT_IMAGE}
            alt="Fresh ingredients"
          />

        </div>


        <div className="theme9-about-copy">

          <span className="theme9-eyebrow">
            GOOD FOOD • GOOD MOOD
          </span>


          <h2>
            Good Food Brings
            <br />
            Good Mood
          </h2>


          <p>
            We believe great food
            starts with great
            ingredients. Everything
            is prepared fresh to
            bring flavour, nutrition
            and happiness to your
            table.
          </p>


          <div className="theme9-about-points">

            <span>
              <Leaf size={17} />
              Fresh ingredients
            </span>

            <span>
              <ChefHat size={17} />
              Made with care
            </span>

            <span>
              <Heart size={17} />
              Healthy choices
            </span>

          </div>


          <button
            type="button"
            className="
              theme9-primary-button
            "
            onClick={
              scrollToMenu
            }
          >

            Explore Food

            <ArrowRight
              size={18}
            />

          </button>

        </div>

      </section>


      {/* ===================================================
          FULL MENU

          Header navigation target:
          #theme9-menu
      ==================================================== */}

      <section
        id="theme9-menu"
        className="theme9-menu"
      >

        <div className="theme9-menu-heading">

          <span className="theme9-eyebrow">
            ORDER YOUR FAVOURITES
          </span>


          <h2>
            Our Full Menu
          </h2>


          <p>
            Fresh meals for breakfast,
            lunch, dinner and everything
            in between.
          </p>

        </div>


        <div className="theme9-menu-layout">


          {/* ===============================================
              CATEGORY SELECTOR
          ================================================ */}

          <aside className="theme9-menu-sidebar">

            {/* CATEGORY BUTTONS */}

            {sortedCategories.map(
              (category) => (

                <button
                  type="button"
                  key={
                    category.id
                  }
                  className={
                    activeCategory ===
                    category.id
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    setActiveCategory(
                      category.id
                    )
                  }
                >

                  <span>
                    {
                      category.name
                    }
                  </span>

                  <small>
                    {
                      category.items
                        ?.length || 0
                    }
                  </small>

                </button>

              )
            )}

          </aside>


          {/* ===============================================
              MENU ITEMS
          ================================================ */}

          <div className="theme9-menu-items">

            {sortedCategories.map(
              (category) => {


                /*
                 * If a category is selected,
                 * only render that category.
                 */

                if (
                  activeCategory !==
                    null &&
                  activeCategory !==
                    category.id
                ) {
                  return null;
                }


                return (

                  <div
                    className="
                      theme9-menu-category
                    "
                    key={
                      category.id
                    }
                  >

                    {/* CATEGORY HEADING */}

                    <div className="
                      theme9-menu-category-heading
                    ">

                      <div>

                        <span>
                          FRESH CHOICES
                        </span>

                        <h3>
                          {
                            category.name
                          }
                        </h3>

                      </div>


                      <small>
                        {
                          category.items
                            .length
                        }{" "}
                        items
                      </small>

                    </div>


                    {/* MENU GRID */}

                    <div className="
                      theme9-menu-list
                    ">

                      {category.items.map(
                        (item) => {

                          const image =
                            getImageUrl(
                              item.image
                            );


                          return (

                            <article
                              className="
                                theme9-menu-item
                              "
                              key={
                                item.id
                              }
                            >

                              {/* ITEM IMAGE */}

                              <div className="
                                theme9-menu-item-image
                              ">

                                {image ? (

                                  <img
                                    src={
                                      image
                                    }
                                    alt={
                                      item.name
                                    }
                                  />

                                ) : (

                                  <div className="
                                    theme9-image-placeholder
                                  ">

                                    <Utensils />

                                  </div>

                                )}

                              </div>


                              {/* ITEM DETAILS */}

                              <div className="
                                theme9-menu-item-details
                              ">

                                <div className="
                                  theme9-menu-item-copy
                                ">

                                  <h3>
                                    {
                                      item.name
                                    }
                                  </h3>

                                  <p>
                                    {
                                      item.description ||
                                      "Freshly prepared with quality ingredients."
                                    }
                                  </p>

                                </div>


                                <div className="
                                  theme9-menu-item-action
                                ">

                                  <strong>
                                    ₹
                                    {Number(
                                      item.price ||
                                        0
                                    ).toFixed(
                                      0
                                    )}
                                  </strong>


                                  <button
                                    type="button"
                                    aria-label={
                                      `Add ${item.name} to cart`
                                    }
                                    onClick={() =>
                                      handleAddToCart(
                                        item
                                      )
                                    }
                                  >

                                    <Plus
                                      size={17}
                                    />

                                    <span>
                                      Add
                                    </span>

                                  </button>

                                </div>

                              </div>

                            </article>

                          );

                        }
                      )}

                    </div>

                  </div>

                );

              }
            )}

          </div>

        </div>

      </section>


      {/* ===================================================
          FINAL CTA / CONTACT TARGET

          Header Contact button scrolls here.

          When Theme 9 Footer.js is created,
          we can move id="theme9-contact"
          to the actual footer instead.
      ==================================================== */}

      <section
        id="theme9-contact"
        className="theme9-final"
      >

        <div className="theme9-final-decoration">

          <Leaf />

        </div>


        <div className="theme9-final-inner">

          <span>
            FRESH FOOD IS WAITING
          </span>


          <h2>
            Hungry for something
            <br />
            fresh?
          </h2>


          <p>
            Pick your favourites
            and we'll take care
            of the rest.
          </p>


          <button
            type="button"
            onClick={
              scrollToMenu
            }
          >

            Order Now

            <ArrowRight
              size={18}
            />

          </button>

        </div>

      </section>


      {/* ===================================================
          FLOATING CART
      ==================================================== */}

      {cartCount > 0 && (

        <button
          type="button"
          className="
            theme9-floating-cart
          "
          onClick={() =>
            navigate("cart")
          }
        >

          <ShoppingCart
            size={19}
          />


          <span className="
            theme9-cart-count
          ">
            {cartCount}
          </span>


          <span>
            View Cart
          </span>


          <ArrowRight
            size={17}
          />

        </button>

      )}


    </main>

  );

}


export default HomeScreen;